import { call, takeLatest, put, select } from 'redux-saga/effects';
import { AxiosResponse } from 'axios';
import * as uuid from 'uuid';
import {
  EDIT_ACCOMMODATION_BOOKING_BUILD_REQUEST,
  EDIT_ACCOMMODATION_OPEN_MODAL,
  EDIT_ACCOMMODATION_SIMULATE_BREAKDOWN_REQUEST,
  EDIT_ACCOMMODATION_FETCH_MEAL_PLANS_REQUEST,
  EDIT_ACCOMMODATION_MANUAL_SAVE_REQUEST,
  editAccommodationBookingBuildFailedAction,
  editAccommodationBookingBuildSuccessAction,
  editAccommodationCloseModalAction,
  editAccommodationFetchMealPlansRequestAction,
  editAccommodationInitiateBookingBuildFailedAction,
  editAccommodationInitiateBookingBuildRequestAction,
  editAccommodationInitiateBookingBuildSuccessAction,
  EditAccommodationOpenModalAction,
  editAccommodationSetBookingBuildAction,
  editAccommodationSetCountryDataAction,
  editAccommodationSetErrorsAction,
  editAccommodationSetInitialErrorsAction,
  editAccommodationSetLastRequestedBuildAction,
  editAccommodationSetOwnershipDataAction,
  editAccommodationSetSelectedMealPlanAction,
  editAccommodationSimulateBreakdownFailedAction,
  EditAccommodationSimulateBreakdownRequestAction,
  editAccommodationSimulateBreakdownSuccessAction,
  updateBreakdownDataAfterAddOrEditAccommodation,
  EditAccommodationFetchMealPlansRequestAction,
  editAccommodationFetchMealPlansSuccessAction,
  editAccommodationFetchMealPlansFailedAction,
  EditAccommodationManualSaveRequestAction,
  editAccommodationManualSaveSuccessAction,
  editAccommodationManualSaveFailedAction,
} from '../actions';
import { format } from 'date-fns';
import { formatDate, formatDateWithoutOffset, tDateString } from 'utils/date';
import * as DateUtilsUtc from 'utils/date.utc';
import * as HotelDetailsSelectors from 'store/modules/bookingManager/subdomains/hotelDetails/selectors';
import { BookingBuilderRequest, BookingBuilderResponse, makeBackendApi } from 'services/BackendApi';
import {
  IBookingCountryResponse,
  IOwnershipResponse,
  ISimulateBreakdownRequest,
  makeBookingManagerApi,
} from 'services/BookingManagerApi';
import * as BreakdownSelectors from '../selectors';
import * as BootstrapSelectors from 'store/modules/bootstrap/selectors';
import { IStaticRate } from 'ui/AddAccommodationModal/MealPlanDropdown/types';
import { customeErrorType, IAccommodationDetails, IHotelMealPlanProduct } from '../model';
import { convertMealPlanToStaticRate } from 'ui/AddAccommodationModal/helpers';
import { isEmpty } from 'lodash-es';
import { enqueueNotification } from 'store/modules/ui';
import { formatPrice } from 'utils';
import { IBookingManagerHotelDetails } from '../../hotelDetails/types';
import * as HotelDetailsActions from '../../hotelDetails/actions';
import {
  convertHeadlineLineItemBreakdownToBackendFormat,
  convertHeadlineLineItemBreakdownToFrontendFormat,
} from 'utils/headlineLineItemBreakdown';
import * as DateUtc from 'utils/date.utc';

// Helper function to group guest ages according to meal plan age options
function groupGuestsByAgeRanges(guestAges: any, mealPlanOptions: IHotelMealPlanProduct['options']) {
  const ageGroups: { name: string; count: number }[] = [];

  // Add adults
  if (guestAges.numberOfAdults > 0) {
    ageGroups.push({
      name: 'Adult',
      count: guestAges.numberOfAdults,
    });
  }

  // Group children by age ranges if meal plan has age options
  if (guestAges.agesOfAllChildren && guestAges.agesOfAllChildren.length > 0 && mealPlanOptions?.ages) {
    const childrenByAgeGroup: { [key: string]: number } = {};

    guestAges.agesOfAllChildren.forEach((childAge: number) => {
      // Find which age group this child belongs to
      const ageGroup = mealPlanOptions.ages.find(group => childAge >= group.ageFrom && childAge <= group.ageTo);

      if (ageGroup) {
        childrenByAgeGroup[ageGroup.name] = (childrenByAgeGroup[ageGroup.name] || 0) + 1;
      } else {
        // If no age group matches, treat as child
        childrenByAgeGroup['Child'] = (childrenByAgeGroup['Child'] || 0) + 1;
      }
    });

    // Add child groups to the result
    Object.entries(childrenByAgeGroup).forEach(([ageName, count]) => {
      ageGroups.push({ name: ageName, count });
    });
  }

  return ageGroups;
}

export function* editAccommodationInitiateBookingBuildSaga(action: EditAccommodationOpenModalAction) {
  try {
    // we've got a React/Redux race condition due to how we're calling actions
    // and re. this saga. I don't know the exact cause, but the fact we were doing
    // API calls in this saga STOPPED the bug happening. Now I've got rid of them, the
    // race condition manifests. This Promise.resolve() is a hack to get around it.
    // Without this Promise.resolve(), the selectedMealPlan below is stale.
    yield Promise.resolve();
    //
    yield put(editAccommodationInitiateBookingBuildRequestAction());
    const bookingManagerApi = makeBookingManagerApi();
    const ownershipData: AxiosResponse<IOwnershipResponse> = yield call(
      bookingManagerApi.getOwnership,
      action.bookingUuid
    );
    const bookingCountryData: AxiosResponse<IBookingCountryResponse> = yield call(
      bookingManagerApi.getBookingCountryCode,
      action.bookingUuid
    );
    const clientCountryCode = bookingCountryData.data.countryCode;
    yield put(editAccommodationSetCountryDataAction(bookingCountryData.data));
    yield put(editAccommodationSetOwnershipDataAction(ownershipData.data));

    const travelAgentUuid = ownershipData.data.ownershipData.taInfo.uuid;
    const backendApi = makeBackendApi(travelAgentUuid);
    const startDate = yield select(BreakdownSelectors.editAccommodationStartDateSelector);
    const endDate = yield select(BreakdownSelectors.editAccommodationEndDateSelector);
    const accommodationDetails: IAccommodationDetails = yield select(
      BreakdownSelectors.editAccommodationAccommodationDetailsSelector
    );
    const selectedOccasions = yield select(BreakdownSelectors.editAccommodationOccasionsSelector);
    const repeatCustomer = yield select(BreakdownSelectors.editAccommodationIsRepeatGuestSelector);
    const guestAges = yield select(BreakdownSelectors.editAccommodationGuestAgesSelector);

    if (!accommodationDetails.headlineLineItemBreakdownAccommodationLineItem.requestMeta) {
      throw new Error('This accommodation cannot be edited - No request meta');
    }
    const accommodation =
      accommodationDetails.headlineLineItemBreakdownAccommodationLineItem.requestMeta.requestSegment.data.attributes
        .Accommodation[0];

    const hotelUuidFromRequestSegment = accommodationDetails.headlineLineItemBreakdownAccommodationLineItem.requestMeta
      .requestSegment.data.attributes.hotelUuid as string;

    const bookingBuilderRequest: BookingBuilderRequest = {
      startDate,
      endDate: DateUtc.parseAsUTC(endDate),
      guestAges,
      hotelUuid: hotelUuidFromRequestSegment,
      Accommodation: [
        {
          uuid: accommodation.uuid,
          guestAges,
          repeatCustomer,
          startDate,
          endDate: DateUtc.parseAsUTC(endDate),
          subProducts: {
            'Meal Plan': accommodation.subProducts['Meal Plan'],
            Supplement: [],
          },
          honeymoon: selectedOccasions.includes('honeymoon'),
          anniversary: selectedOccasions.includes('anniversary'),
          wedding: selectedOccasions.includes('wedding'),
          birthday: selectedOccasions.includes('birthday'),
        },
      ],
      Transfer: [],
      customItems: [],
      'Ground Service': [],
      Supplement: [],
      Fine: [],
    };

    const response: AxiosResponse<{ data: BookingBuilderResponse }> = yield call(backendApi.postBookingBuilderRequest, {
      bookingBuilderRequest,
      clientCountryCode,
      travelAgentUuid,
      queryParams: {
        isForBookingBreakdown: true,
      },
    });

    const errors = (response.data.data.errors || []) as customeErrorType;
    if (errors.length > 0) {
      yield put(editAccommodationSetInitialErrorsAction(errors));

      // Check if we're entering manual edit mode (errors that would block editing)
      const hasStopErrors = errors.some(error => error.message.includes('No Accommodation Product rates'));

      if (hasStopErrors) {
        // Fetch meal plans for manual edit mode
        yield put(editAccommodationFetchMealPlansRequestAction(hotelUuidFromRequestSegment));
      }
    }
    const selectedMealPlan = response.data.data.availableProductSets.Accommodation[0].availableSubProductSets[
      'Meal Plan'
    ].find(mp => mp.breakdown[0].product.uuid === accommodation.subProducts['Meal Plan'][0].uuid);
    const selectedStaticRate = convertMealPlanToStaticRate(selectedMealPlan);

    response.data.data.totals.total = formatPrice(
      ((action.accommodationDetails.headlineLineItemBreakdownAccommodationLineItem.saleCostCents ?? 0) +
        (action.accommodationDetails.headlineLineItemBreakdownAccommodationLineItem['Meal Plan'].subtotalCents ?? 0)) /
        100
    );

    yield put(
      editAccommodationSetSelectedMealPlanAction(!isEmpty(selectedStaticRate) ? selectedStaticRate : undefined)
    );
    yield put(editAccommodationSetBookingBuildAction(response.data.data));
    yield put(editAccommodationSetLastRequestedBuildAction(bookingBuilderRequest));
    yield put(editAccommodationInitiateBookingBuildSuccessAction());
  } catch (e) {
    yield put(editAccommodationInitiateBookingBuildFailedAction());
    console.error('Error', e);
  }
}

export function* editAccommodationBookingBuildSaga() {
  try {
    // we've got a React/Redux race condition due to how we're calling actions
    // and re. this saga. I don't know the exact cause, but the fact we were doing
    // API calls in this saga STOPPED the bug happening. Now I've got rid of them, the
    // race condition manifests. This Promise.resolve() is a hack to get around it.
    // Without this Promise.resolve(), the selectedMealPlan below is stale.
    yield Promise.resolve();
    //

    const ownershipData: IOwnershipResponse | null = yield select(
      BreakdownSelectors.editAccommodationOwnershipDataSelector
    );
    const countryData: IBookingCountryResponse | null = yield select(
      BreakdownSelectors.editAccommodationCountryDataSelector
    );
    const clientCountryCode = countryData?.countryCode;
    const travelAgentUuid = ownershipData?.ownershipData.taInfo.uuid;
    const backendApi = makeBackendApi(travelAgentUuid);
    const startDate = yield select(BreakdownSelectors.editAccommodationStartDateSelector);
    const endDate = yield select(BreakdownSelectors.editAccommodationEndDateSelector);

    console.log('edit accom saga');
    console.log('startDate', startDate);
    console.log('endDate', endDate);
    const accommodationDetails: IAccommodationDetails = yield select(
      BreakdownSelectors.editAccommodationAccommodationDetailsSelector
    );
    const selectedOccasions = yield select(BreakdownSelectors.editAccommodationOccasionsSelector);
    const selectedMealPlan = (yield select(BreakdownSelectors.editAccommodationSelectedMealPlanSelector)) as
      | IStaticRate
      | undefined;
    const repeatCustomer = yield select(BreakdownSelectors.editAccommodationIsRepeatGuestSelector);
    const guestAges = yield select(BreakdownSelectors.editAccommodationGuestAgesSelector);

    const accommodation =
      accommodationDetails.headlineLineItemBreakdownAccommodationLineItem.requestMeta?.requestSegment.data.attributes
        .Accommodation[0];

    const hotelUuidFromRequestSegment = accommodationDetails.headlineLineItemBreakdownAccommodationLineItem.requestMeta
      ?.requestSegment.data.attributes.hotelUuid as string;

    const bookingBuilderRequest: BookingBuilderRequest = {
      startDate: DateUtc.parseAsUTC(startDate),
      endDate: DateUtc.parseAsUTC(endDate),
      guestAges,
      hotelUuid: hotelUuidFromRequestSegment,
      Accommodation: [
        {
          uuid: accommodation.uuid,
          guestAges,
          repeatCustomer,
          startDate: DateUtc.parseAsUTC(startDate),
          endDate: DateUtc.parseAsUTC(endDate),
          subProducts: {
            'Meal Plan': selectedMealPlan?.mealPlan.uuids.map(uuid => ({ uuid })),
            Supplement: [],
          },
          honeymoon: selectedOccasions.includes('honeymoon'),
          anniversary: selectedOccasions.includes('anniversary'),
          wedding: selectedOccasions.includes('wedding'),
          birthday: selectedOccasions.includes('birthday'),
        },
      ],
      Transfer: [],
      customItems: [],
      'Ground Service': [],
      Supplement: [],
      Fine: [],
    };
    const response: AxiosResponse<{ data: BookingBuilderResponse }> = yield call(backendApi.postBookingBuilderRequest, {
      bookingBuilderRequest,
      clientCountryCode,
      travelAgentUuid,
      queryParams: {
        isForBookingBreakdown: true,
      },
    });

    const errors = (response.data.data.errors || []) as customeErrorType;
    if (errors.length > 0) {
      yield put(editAccommodationSetErrorsAction(errors));
    }
    yield put(editAccommodationSetBookingBuildAction(response.data.data));
    yield put(editAccommodationSetLastRequestedBuildAction(bookingBuilderRequest));
    yield put(editAccommodationBookingBuildSuccessAction());
  } catch (e) {
    yield put(editAccommodationBookingBuildFailedAction());
    console.error('Error', e);
  }
}

export function* editAccommodationSaveUpdatesSaga(action: EditAccommodationSimulateBreakdownRequestAction) {
  try {
    const bookingManagerApi = makeBookingManagerApi();
    const selectedBuild = yield select(BreakdownSelectors.editAccommodationSelectedBuildSelector);
    const lastRequestedBuild = yield select(BreakdownSelectors.editAccommodationLastRequestedBuildSelector);
    const headlineLineItemBreakdown = yield select(BreakdownSelectors.headlineLineItemBreakdownSelector);
    const countryData = yield select(BreakdownSelectors.editAccommodationCountryDataSelector);
    const cancellationPolicies = yield select(BreakdownSelectors.cancellationPoliciesSelector)!;
    const paymentTerms = yield select(BreakdownSelectors.paymentTermsSelector)!;
    const policiesAndRestrictions = yield select(BreakdownSelectors.policiesAndRestrictionsSelector)!;
    const offerTerms = yield select(BreakdownSelectors.offerTermsSelector)!;
    const accomIndex = yield select(BreakdownSelectors.editAccommodationAccommodationIndexSelector)!;
    const hotelDetails: IBookingManagerHotelDetails[] = yield select(HotelDetailsSelectors.hotelDetailsSelector);
    const backendHeadlineLineItemBreakdown = convertHeadlineLineItemBreakdownToBackendFormat(headlineLineItemBreakdown);
    const replaceIndex = backendHeadlineLineItemBreakdown.Accommodation.items.findIndex(
      item => item.uuid === accomIndex
    );

    const simulateBreakdownRequest: ISimulateBreakdownRequest = {
      bookingBuild: selectedBuild,
      requestedBuild: lastRequestedBuild,
      userCountryCode: countryData,
      headlineLineItemBreakdown: backendHeadlineLineItemBreakdown,
      cancellationPolicies,
      paymentTerms,
      policiesAndRestrictions,
      offerTerms,
      hotelDetails: {
        hotelDetails,
      },
      replaceItems: {
        Accommodation: replaceIndex,
      },
    };

    const res = (yield call(
      bookingManagerApi.postSimulateBreakdown,
      action.bookingUuid,
      simulateBreakdownRequest
    )) as AxiosResponse;
    yield put(
      updateBreakdownDataAfterAddOrEditAccommodation(
        convertHeadlineLineItemBreakdownToFrontendFormat(res.data.headlineLineItemBreakdown),
        res.data.cancellationPolicies,
        res.data.paymentTerms,
        res.data.policiesAndRestrictions,
        res.data.offerTerms
      )
    );

    yield put(HotelDetailsActions.setHotelDetailsAction(res.data.hotelDetails));
    yield put(
      enqueueNotification({
        message: 'Accommodation edit successfully.',
        options: { variant: 'success' },
      })
    );
    yield put(editAccommodationSimulateBreakdownSuccessAction());
    yield put(editAccommodationCloseModalAction());
  } catch (e) {
    yield put(editAccommodationSimulateBreakdownFailedAction());
    yield put(
      enqueueNotification({
        message: 'Accommodation failed to edit. Please try again.',
        options: { variant: 'error' },
      })
    );
    console.error('Error', e);
  }
}

export function* editAccommodationFetchMealPlansSaga(action: EditAccommodationFetchMealPlansRequestAction) {
  try {
    const ownershipData: IOwnershipResponse | null = yield select(
      BreakdownSelectors.editAccommodationOwnershipDataSelector
    );
    const travelAgentUuid = ownershipData?.ownershipData.taInfo.uuid;
    const backendApi = makeBackendApi(travelAgentUuid);

    const response: AxiosResponse<{ data: IHotelMealPlanProduct[] }> = yield call(
      backendApi.getHotelMealPlans,
      action.hotelUuid
    );

    console.log('response.data.data', response.data.data);

    // Store the full product data for use in manual save
    yield put(editAccommodationFetchMealPlansSuccessAction(response.data.data));
  } catch (e) {
    yield put(editAccommodationFetchMealPlansFailedAction(e));
    console.error('Error fetching meal plans', e);
  }
}

export function* editAccommodationManualSaveSaga(_action: EditAccommodationManualSaveRequestAction) {
  try {
    // Get current state
    const startDate = yield select(BreakdownSelectors.editAccommodationStartDateSelector);
    let endDate = yield select(BreakdownSelectors.editAccommodationEndDateSelector);
    endDate = DateUtilsUtc.addDays(formatDate(endDate) as tDateString, 1);
    const selectedMealPlan = yield select(BreakdownSelectors.editAccommodationSelectedMealPlanSelector);
    const guestAges = yield select(BreakdownSelectors.editAccommodationGuestAgesSelector);
    const headlineLineItemBreakdown = yield select(BreakdownSelectors.headlineLineItemBreakdownSelector);
    const accomIndex = yield select(BreakdownSelectors.editAccommodationAccommodationIndexSelector);
    const allHotelMealPlans: IHotelMealPlanProduct[] = yield select(
      BreakdownSelectors.editAccommodationAllHotelMealPlansSelector
    );
    const epsProduct = yield select(BootstrapSelectors.getBootstrapExtraPersonSupplementProductSelector);

    if (!headlineLineItemBreakdown || !accomIndex || !selectedMealPlan) {
      throw new Error('Missing required data for manual save');
    }

    // Create a copy of the breakdown to modify
    const updatedBreakdown = { ...headlineLineItemBreakdown };
    const accommodationItem = updatedBreakdown.Accommodation.items[accomIndex];

    if (!accommodationItem) {
      throw new Error('Accommodation item not found');
    }

    // Find the selected meal plan product from the hotel meal plans
    const selectedHotelMealPlan = allHotelMealPlans.find(mp => mp.uuid === selectedMealPlan.mealPlan.uuids[0]);

    // Update accommodation dates and title
    const formattedStartDate = formatDateWithoutOffset(startDate, 'MMM dd');
    const formattedEndDate = formatDateWithoutOffset(endDate, 'MMM dd');
    accommodationItem.arrival = formatDate(startDate);
    accommodationItem.departure = formatDate(endDate);
    accommodationItem.title = accommodationItem.title.replace(
      /\w{3} \d{2} - \w{3} \d{2}/,
      `${formattedStartDate} - ${formattedEndDate}`
    );

    // Get the original meal plan item for reference (to copy pricing and other data)
    const originalMealPlanItem = accommodationItem['Meal Plan']?.items[0];

    // Clear all existing meal plan items
    accommodationItem['Meal Plan'].items = [];

    // Create new meal plan rows based on guest ages
    if (selectedHotelMealPlan && originalMealPlanItem) {
      const ageGroups = groupGuestsByAgeRanges(guestAges, selectedHotelMealPlan.options);

      // Create a meal plan row for each age group
      ageGroups.forEach(ageGroup => {
        const newMealPlanItem = {
          ...originalMealPlanItem, // Copy all original properties
          uuid: uuid.v4(), // Generate new UUID
          title: selectedHotelMealPlan.name,
          tertiaryText: `${ageGroup.count} * ${ageGroup.name}`,
          // Update subProductMetaData if it exists
          supplierData: originalMealPlanItem.supplierData
            ? {
                ...originalMealPlanItem.supplierData,
                product: {
                  ...originalMealPlanItem.supplierData.product,
                  uuid: selectedHotelMealPlan.uuid,
                },
                subProductMetaData: selectedHotelMealPlan.meta
                  ? {
                      description:
                        selectedHotelMealPlan.meta.description ||
                        originalMealPlanItem.supplierData.subProductMetaData?.description,
                      categoryType:
                        selectedHotelMealPlan.meta.categoryType ||
                        originalMealPlanItem.supplierData.subProductMetaData?.categoryType,
                    }
                  : originalMealPlanItem.supplierData.subProductMetaData,
              }
            : originalMealPlanItem.supplierData,
        };

        accommodationItem['Meal Plan'].items.push(newMealPlanItem);
      });
    }

    // Remove Extra Person Supplement (EPS) supplements but keep all others
    if (epsProduct && accommodationItem.Supplement?.items) {
      accommodationItem.Supplement.items = accommodationItem.Supplement.items.filter(
        supplement => supplement.productUuid !== epsProduct.uuid
      );
    }

    // Clear all costing fields to 0 for accommodation and all meal plans/supplements
    // This ensures manual pricing mode - all costs must be manually entered
    accommodationItem.companyExternalMarkupAmountCents = 0;
    accommodationItem.costBeforeDiscountCents = 0;
    accommodationItem.internalMarkupAmountCents = 0;
    accommodationItem.purchaseCostCents = 0;
    accommodationItem.saleCostCents = 0;

    // Clear costing fields for meal plan items
    accommodationItem['Meal Plan'].items.forEach(mealPlanItem => {
      mealPlanItem.companyExternalMarkupAmountCents = 0;
      mealPlanItem.costBeforeDiscountCents = 0;
      mealPlanItem.internalMarkupAmountCents = 0;
      mealPlanItem.purchaseCostCents = 0;
      mealPlanItem.saleCostCents = 0;
    });

    // Clear costing fields for supplement items
    if (accommodationItem.Supplement?.items) {
      accommodationItem.Supplement.items.forEach(supplementItem => {
        supplementItem.companyExternalMarkupAmountCents = 0;
        supplementItem.costBeforeDiscountCents = 0;
        supplementItem.internalMarkupAmountCents = 0;
        supplementItem.purchaseCostCents = 0;
        supplementItem.saleCostCents = 0;
      });
    }

    // Update requestMeta object to synchronize with new manual edit data
    if (accommodationItem.requestMeta?.requestSegment?.data?.attributes) {
      const attributes = accommodationItem.requestMeta.requestSegment.data.attributes;

      // Update top-level date fields
      attributes.startDate = formatDate(startDate);
      attributes.endDate = formatDate(endDate);
      attributes.arrival = formatDate(startDate);

      // Update guest information
      attributes.guestAges = guestAges;
      attributes.numberOfAdults = guestAges.numberOfAdults;

      // Update accommodation array dates (if it exists)
      if (attributes.Accommodation && attributes.Accommodation[0]) {
        attributes.Accommodation[0].startDate = formatDate(startDate);
        attributes.Accommodation[0].endDate = formatDate(endDate);
        attributes.Accommodation[0].guestAges = guestAges;
      }
    }

    // Update the breakdown
    yield put(
      updateBreakdownDataAfterAddOrEditAccommodation(
        updatedBreakdown,
        yield select(BreakdownSelectors.cancellationPoliciesSelector),
        yield select(BreakdownSelectors.paymentTermsSelector),
        yield select(BreakdownSelectors.policiesAndRestrictionsSelector),
        yield select(BreakdownSelectors.offerTermsSelector)
      )
    );

    // Show success notification
    yield put(
      enqueueNotification({
        message: 'Accommodation edited successfully.',
        options: { variant: 'success' },
      })
    );

    yield put(editAccommodationManualSaveSuccessAction());
    yield put(editAccommodationCloseModalAction());
  } catch (e) {
    yield put(editAccommodationManualSaveFailedAction());
    yield put(
      enqueueNotification({
        message: 'Accommodation failed to edit. Please try again.',
        options: { variant: 'error' },
      })
    );
    console.error('Error in manual save', e);
  }
}

export function* watchEditAccommodationBookingBuildSaga() {
  yield takeLatest([EDIT_ACCOMMODATION_OPEN_MODAL], editAccommodationInitiateBookingBuildSaga);
  yield takeLatest([EDIT_ACCOMMODATION_BOOKING_BUILD_REQUEST], editAccommodationBookingBuildSaga);
  yield takeLatest([EDIT_ACCOMMODATION_SIMULATE_BREAKDOWN_REQUEST], editAccommodationSaveUpdatesSaga);
  yield takeLatest([EDIT_ACCOMMODATION_FETCH_MEAL_PLANS_REQUEST], editAccommodationFetchMealPlansSaga);
  yield takeLatest([EDIT_ACCOMMODATION_MANUAL_SAVE_REQUEST], editAccommodationManualSaveSaga);
}
